import type { SiteSectionCategoryId, SiteSectionId, V4HSiteId } from "@/data/common/ids";
import type { DateRange } from "@/domains/v4b/site-consumption/site-consumption.types";

import { useQuery } from "@tanstack/vue-query";
import { computed, MaybeRefOrGetter } from "vue";
import { toRef } from "vue";

import { useServices } from "@/configuration/plugins/app/app-provider.configuration";
import { ConsumptionQueryKeys } from "@/data/api/kili/consumption/consumption-query-keys";
import KiliApi from "@/data/api/kili/kili-api";

export function useDashboardMonositeQueries(
  siteId: MaybeRefOrGetter<V4HSiteId>,
  dateRange: MaybeRefOrGetter<DateRange>,
  requestedSectionIds?: MaybeRefOrGetter<Array<SiteSectionId>>,
  requestedSectionCategoryIds?: MaybeRefOrGetter<Array<SiteSectionCategoryId>>,
) {
  const _siteId = toRef(siteId);
  const _dateRange = toRef(dateRange);
  const _requestedSectionIds = toRef(requestedSectionIds);
  const _requestedSectionCategoryIds = toRef(requestedSectionCategoryIds);

  const v4b = useServices().v4b.clientSdk;

  const formattedRequestedSectionIds = computed<Array<SiteSectionId> | null>(() => {
    if (!_requestedSectionIds.value || !_requestedSectionIds.value.length) return null;
    return _requestedSectionIds.value;
  });

  const formattedRequestedSectionCategoryIds = computed<Array<SiteSectionCategoryId> | null>(() => {
    if (!_requestedSectionCategoryIds.value || !_requestedSectionCategoryIds.value.length) return null;
    return _requestedSectionCategoryIds.value;
  });

  const {
    data: missingInfos,
    isPending: isMissingInfosPending,
    error: siteMissingInfosError,
  } = useQuery({
    queryKey: ConsumptionQueryKeys.siteConsumptionMissingInfos(_siteId),
    queryFn: async () => KiliApi.getSiteMissingConsumptionInfos({ params: { siteId: _siteId.value } }),
    enabled: computed(() => _siteId.value > 0),
  });

  const {
    data: siteConsumption,
    isPending: isSiteConsumptionPending,
    error: siteConsumptionError,
  } = useQuery({
    queryKey: ConsumptionQueryKeys.siteConsumption(
      _siteId,
      _dateRange,
      _requestedSectionIds,
      _requestedSectionCategoryIds,
    ),
    queryFn: async () =>
      v4b.siteConsumption.findSiteConsumption(_siteId.value, {
        dateRange: _dateRange.value,
        requestedSectionIds: formattedRequestedSectionIds.value,
        requestedSectionCategoryIds: formattedRequestedSectionCategoryIds.value,
      }),
    enabled: computed(() => _siteId.value > 0),
    // theorically, past consumption is not meant to change
    staleTime: Infinity,
  });

  return {
    siteConsumption,
    missingInfos,
    siteMissingInfosError,
    isSiteConsumptionPending,
    isMissingInfosPending,
    siteConsumptionError,
  };
}
